import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../lib/content";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Over hero (top of any page): show transparent on white text.
  // After scroll: white background, dark text.
  const dark = scrolled; // dark = "ink mode" navigation
  const navTextBase = dark ? "text-[#0A0A0A]/70" : "text-white/85";
  const navTextActive = dark ? "text-[#0A0A0A]" : "text-[#C9A86A]";
  const logoMain = dark ? "text-[#0A0A0A]" : "text-white";
  const ctaBg = "bg-[#C9A86A] text-black hover:bg-[#B8965A]";
  const mobileBtnBorder = dark ? "border-[#0A0A0A]/20 text-[#0A0A0A]" : "border-white/30 text-white";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-black/10 shadow-[0_8px_30px_-20px_rgba(0,0,0,0.15)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2 group">
          <span className={`font-display-bold text-xl md:text-2xl tracking-tight ${logoMain}`}>
            REFLEX
          </span>
          <span className="font-serif-italic text-[#C9A86A] text-base md:text-lg -ml-1">
            stone&nbsp;care
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9" data-testid="primary-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s|\//g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-[0.18em] transition-colors ${
                  isActive ? navTextActive : `${navTextBase} hover:${dark ? "text-[#0A0A0A]" : "text-white"}`
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BRAND.phoneHref}
            data-testid="header-call-btn"
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors ${ctaBg}`}
          >
            <Phone className="w-3.5 h-3.5" />
            {BRAND.phone}
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden inline-flex items-center justify-center w-10 h-10 border ${mobileBtnBorder}`}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-white border-t border-black/10"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mnav-${l.label.toLowerCase().replace(/\s|\//g, "-")}`}
                  className={({ isActive }) =>
                    `text-base ${isActive ? "text-[#C9A86A]" : "text-[#0A0A0A]/85"}`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={BRAND.phoneHref}
                data-testid="mobile-menu-call-btn"
                className="mt-4 inline-flex items-center justify-center gap-2 py-3 bg-[#C9A86A] text-black uppercase tracking-[0.2em] text-xs"
              >
                <Phone className="w-4 h-4" /> {BRAND.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
